/**
 * ⛵ YACHT SELECTOR COMPONENT
 * Reusable component for searching and selecting yachts in booking dialogs
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  InputAdornment,
  Grid,
  Card,
  CardMedia,
  CardContent,
  Typography,
  Chip,
  Stack,
  CircularProgress,
  Alert,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import {
  DirectionsBoat,
  Search,
  People,
  Hotel,
  AttachMoney,
  Close,
} from '@mui/icons-material';
import { yachtApprovalService, PendingYacht } from '@/services/yachtApprovalService';

interface YachtSelectorProps {
  selectedYacht: PendingYacht | null;
  onSelectYacht: (yacht: PendingYacht) => void;
  label?: string;
  required?: boolean;
  disabled?: boolean;
}

export const YachtSelector: React.FC<YachtSelectorProps> = ({
  selectedYacht,
  onSelectYacht,
  label = 'Select Yacht',
  required = true,
  disabled = false,
}) => {
  const [open, setOpen] = useState(false);
  const [yachts, setYachts] = useState<PendingYacht[]>([]);
  const [filteredYachts, setFilteredYachts] = useState<PendingYacht[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedType, setSelectedType] = useState('');
  const [selectedCapacity, setSelectedCapacity] = useState('');

  // Load yachts when dialog opens
  useEffect(() => {
    if (open) {
      loadYachts();
    }
  }, [open]);

  // Filter yachts when search or filters change
  useEffect(() => {
    let filtered = yachts;

    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter((y) => {
        const name = y.yacht.name?.toLowerCase() || '';
        const type = y.yacht.type?.toLowerCase() || '';
        const homePort = y.yacht.homePort?.toLowerCase() || '';
        return name.includes(query) || type.includes(query) || homePort.includes(query);
      });
    }

    // Type filter
    if (selectedType) {
      filtered = filtered.filter((y) => y.yacht.type === selectedType);
    }

    // Capacity filter
    if (selectedCapacity) {
      const capacity = parseInt(selectedCapacity);
      filtered = filtered.filter((y) => y.yacht.guestCapacity >= capacity);
    }

    setFilteredYachts(filtered);
  }, [yachts, searchQuery, selectedType, selectedCapacity]);

  const loadYachts = async () => {
    setLoading(true);
    try {
      const response = await yachtApprovalService.getPendingYachts({
        status: 'approved',
        limit: 100,
      });
      if (response.success && response.data?.yachts) {
        setYachts(response.data.yachts);
        setFilteredYachts(response.data.yachts);
      }
    } catch (error) {
      console.error('Error loading yachts:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectYacht = (yacht: PendingYacht) => {
    onSelectYacht(yacht);
    setOpen(false);
  };

  const handleClearSelection = () => {
    onSelectYacht(null as any);
  };

  const formatCurrency = (amount: number) => {
    return `₦${amount.toLocaleString('en-NG', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  };

  const getPrimaryPhoto = (yacht: PendingYacht) => {
    // Find the primary photo first
    const primaryPhoto = yacht.photos?.items?.find((p) => p.isPrimary);
    if (primaryPhoto?.url) return primaryPhoto.url;

    // Fall back to first photo
    if (yacht.photos?.items?.[0]?.url) return yacht.photos.items[0].url;

    // Default placeholder
    return '/placeholder-yacht.jpg';
  };

  // Get unique yacht types
  const yachtTypes = Array.from(new Set(yachts.map((y) => y.yacht.type).filter(Boolean)));

  // Get capacity options
  const capacityOptions = [10, 20, 30, 50, 100];

  return (
    <Box>
      {/* Selected Yacht Display */}
      {selectedYacht && (
        <Paper sx={{ p: 2, mb: 2, bgcolor: 'success.50', border: '1px solid', borderColor: 'success.main' }}>
          <Stack direction="row" spacing={2} alignItems="center">
            <DirectionsBoat color="success" />
            <Box sx={{ flex: 1 }}>
              <Typography variant="body1" fontWeight={600}>
                {selectedYacht.yacht.name}
              </Typography>
              <Stack direction="row" spacing={2} sx={{ mt: 0.5 }}>
                <Typography variant="caption" color="text.secondary">
                  {selectedYacht.yacht.type}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {selectedYacht.yacht.length}ft
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  <People sx={{ fontSize: 14, mr: 0.5, verticalAlign: 'middle' }} />
                  {selectedYacht.yacht.guestCapacity} guests
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  <Hotel sx={{ fontSize: 14, mr: 0.5, verticalAlign: 'middle' }} />
                  {selectedYacht.yacht.cabins} cabins
                </Typography>
              </Stack>
            </Box>
            <Button size="small" onClick={handleClearSelection} disabled={disabled}>
              Change
            </Button>
          </Stack>
        </Paper>
      )}

      {/* Select Yacht Button (only show when no yacht selected) */}
      {!selectedYacht && (
        <Button
          fullWidth
          variant="outlined"
          startIcon={<DirectionsBoat />}
          onClick={() => setOpen(true)}
          disabled={disabled}
          sx={{
            py: 1.5,
            justifyContent: 'flex-start',
            color: 'text.secondary',
            borderColor: 'divider',
          }}
        >
          {label} {required && '*'}
        </Button>
      )}

      {/* Yacht Selection Dialog */}
      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="lg" fullWidth>
        <DialogTitle>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Stack direction="row" spacing={1} alignItems="center">
              <DirectionsBoat />
              <Typography variant="h6">Select Yacht</Typography>
            </Stack>
            <Button onClick={() => setOpen(false)} size="small">
              <Close />
            </Button>
          </Stack>
        </DialogTitle>

        <DialogContent dividers>
          {/* Filters */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    placeholder="Search by name, type, or home port..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Search />
                        </InputAdornment>
                      ),
                    }}
                    size="small"
                  />
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Yacht Type</InputLabel>
                    <Select
                      value={selectedType}
                      label="Yacht Type"
                      onChange={(e) => setSelectedType(e.target.value)}
                    >
                      <MenuItem value="">All Types</MenuItem>
                      {yachtTypes.map((type) => (
                        <MenuItem key={type} value={type}>
                          {type}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Min Capacity</InputLabel>
                    <Select
                      value={selectedCapacity}
                      label="Min Capacity"
                      onChange={(e) => setSelectedCapacity(e.target.value)}
                    >
                      <MenuItem value="">All Capacities</MenuItem>
                      {capacityOptions.map((capacity) => (
                        <MenuItem key={capacity} value={capacity.toString()}>
                          {capacity}+ guests
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Loading State */}
          {loading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          )}

          {/* No Results */}
          {!loading && filteredYachts.length === 0 && (
            <Alert severity="info">
              No yachts found matching your criteria. Try adjusting the filters.
            </Alert>
          )}

          {/* Yacht Grid */}
          {!loading && filteredYachts.length > 0 && (
            <Grid container spacing={3}>
              {filteredYachts.map((yacht) => (
                <Grid item xs={12} sm={6} md={4} key={yacht.id}>
                  <Card
                    sx={{
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                      '&:hover': {
                        boxShadow: 4,
                        transform: 'translateY(-4px)',
                      },
                    }}
                    onClick={() => handleSelectYacht(yacht)}
                  >
                    {/* Yacht Image */}
                    <CardMedia
                      component="img"
                      height="200"
                      image={getPrimaryPhoto(yacht)}
                      alt={yacht.yacht.name}
                      sx={{ objectFit: 'cover' }}
                    />

                    <CardContent sx={{ flexGrow: 1 }}>
                      {/* Yacht Name & Type */}
                      <Typography variant="h6" gutterBottom>
                        {yacht.yacht.name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        {yacht.yacht.type}
                      </Typography>

                      {/* Yacht Details */}
                      <Stack spacing={1} sx={{ mt: 2 }}>
                        <Stack direction="row" justifyContent="space-between">
                          <Typography variant="caption" color="text.secondary">
                            Length
                          </Typography>
                          <Typography variant="caption" fontWeight={600}>
                            {yacht.yacht.length}ft
                          </Typography>
                        </Stack>
                        <Stack direction="row" justifyContent="space-between">
                          <Typography variant="caption" color="text.secondary">
                            Capacity
                          </Typography>
                          <Typography variant="caption" fontWeight={600}>
                            {yacht.yacht.guestCapacity} guests
                          </Typography>
                        </Stack>
                        <Stack direction="row" justifyContent="space-between">
                          <Typography variant="caption" color="text.secondary">
                            Cabins
                          </Typography>
                          <Typography variant="caption" fontWeight={600}>
                            {yacht.yacht.cabins}
                          </Typography>
                        </Stack>
                        <Stack direction="row" justifyContent="space-between">
                          <Typography variant="caption" color="text.secondary">
                            Home Port
                          </Typography>
                          <Typography variant="caption" fontWeight={600}>
                            {yacht.yacht.homePort}
                          </Typography>
                        </Stack>
                      </Stack>

                      {/* Charter Pricing */}
                      {yacht.pricing && (() => {
                        // Handle both snake_case (API) and camelCase (TypeScript)
                        const pricing = yacht.pricing as any;
                        const hourlyRate = pricing.hourly_rate || pricing.hourlyRate;
                        const halfDayRate = pricing.half_day_rate || pricing.halfDayRate;
                        const fullDayRate = pricing.full_day_rate || pricing.fullDayRate || pricing.charterRate;
                        const weeklyRate = pricing.weekly_rate || pricing.weeklyRate;
                        const minimumHours = pricing.minimum_booking_hours || pricing.minimumBookingHours;

                        return (
                          <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
                            <Typography variant="caption" color="text.secondary" gutterBottom display="block">
                              Charter Rates
                            </Typography>
                            <Stack spacing={0.5} sx={{ mt: 1 }}>
                              {hourlyRate && (
                                <Stack direction="row" justifyContent="space-between">
                                  <Typography variant="caption">Hourly:</Typography>
                                  <Typography variant="caption" fontWeight={600} color="primary">
                                    {formatCurrency(hourlyRate)}
                                  </Typography>
                                </Stack>
                              )}
                              {halfDayRate && (
                                <Stack direction="row" justifyContent="space-between">
                                  <Typography variant="caption">Half Day:</Typography>
                                  <Typography variant="caption" fontWeight={600} color="primary">
                                    {formatCurrency(halfDayRate)}
                                  </Typography>
                                </Stack>
                              )}
                              {fullDayRate && (
                                <Stack direction="row" justifyContent="space-between">
                                  <Typography variant="caption">Full Day:</Typography>
                                  <Typography variant="caption" fontWeight={600} color="primary">
                                    {formatCurrency(fullDayRate)}
                                  </Typography>
                                </Stack>
                              )}
                              {weeklyRate && (
                                <Stack direction="row" justifyContent="space-between">
                                  <Typography variant="caption">Weekly:</Typography>
                                  <Typography variant="caption" fontWeight={600} color="primary">
                                    {formatCurrency(weeklyRate)}
                                  </Typography>
                                </Stack>
                              )}
                              {!hourlyRate && !halfDayRate && !fullDayRate && !weeklyRate && (
                                <Typography variant="caption" color="text.secondary">
                                  Contact for pricing
                                </Typography>
                              )}
                            </Stack>
                            {minimumHours && (
                              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                                Min. {minimumHours} hours
                              </Typography>
                            )}
                          </Box>
                        );
                      })()}
                    </CardContent>

                    {/* Select Button */}
                    <Box sx={{ p: 2, pt: 0 }}>
                      <Button fullWidth variant="contained">
                        Select Yacht
                      </Button>
                    </Box>
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default YachtSelector;
